import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import type { SelectChangeEvent } from '@mui/material';
import * as d3 from 'd3';

import { useContainerWidth } from '../../../hooks/useContainerWidth';
import { useAxisFit } from '../../../hooks/useAxisFit';
import { useSignalInteraction } from '../../../context/SignalInteractionContext';
import { SectionCard, PanelHeader } from '../../shared';
import type {
    OHLCVPoint,
    SignalOverlayData,
    SignalItem,
    AlertsOverlayData,
} from '../../../api/types';
import type { MarketSeriesData } from '../../../api/marketPricesClient';
import type { AssetOverlayData } from '../../../hooks/useAssetPriceOverlay';
import type {
    ConvictionSeries,
    ConvictionTimePoint,
} from '../../../api/useConvictionTimeseries';
import { canShowCandles, displayedConditionIds } from '../chartCandles';
import { makeTooltipColors } from '../chartTooltips';
import type { ExportSeries } from '../ExportButton';
import type { TradeOverlayData } from '../chartTypes';
import {
    outcomeLabel,
    isNeutralNoSide,
    OUTCOME_MAXLEN_BADGE,
} from '../../../utils/outcomeLabels';

import {
    computeLayout,
    MARGIN,
    TOTAL_HEIGHT,
    MARKER_OVERFLOW,
} from './layout';
import {
    EMPTY_NO_SERIES,
    type CandleResolution,
    type ConvictionMetric,
    type EffectiveChartType,
    type MarketLineMeta,
} from './constants';
import type { ChartColors, DrawCtx } from './drawContext';
import {
    quantizedNow,
    extendSeriesToNow,
    computeFullDomain,
    makeXScale,
    computePanBounds,
    makeCandleDataFor,
    computeProbDomain,
    makeYScale,
} from './scales';
import { drawXAxis, drawProbabilityAxis, xTickFormat } from './panels/drawAxes';
import { drawVolumePanel } from './panels/drawVolumePanel';
import { drawProbabilityPanel } from './panels/drawProbabilityPanel';
import { drawAssetOverlays } from './panels/drawAssetOverlays';
import { drawPositionPanel } from './panels/drawPositionPanel';
import { drawConvictionPanel } from './panels/drawConvictionPanel';
import { drawSignalOverlay } from './overlays/drawSignalOverlay';
import {
    drawTradeOverlay,
    type OverlayStats,
} from './overlays/drawTradeOverlay';
import { drawAlertOverlay } from './overlays/drawAlertOverlay';
import { useChartViewport } from './interaction/useChartViewport';
import { attachViewport } from './interaction/attachViewport';
import { createChrome } from './interaction/chrome';
import { attachCrosshair } from './interaction/attachCrosshair';
import { ChartHeaderControls } from './ui/ChartHeaderControls';
import {
    EventProgressBar,
    computeEventProgress,
} from './ui/EventProgressBar';
import { MarketLegend } from './ui/MarketLegend';
import { TooltipBox } from './ui/TooltipBox';

// Re-exported for import sites that historically took these from here.
export type { TradeOverlayData } from '../chartTypes';
export type { MarketLineMeta, CandleResolution, ConvictionMetric };

// ─── Props ────────────────────────────────────────────────────────────────────

interface ConvergenceChartProps {
    series: MarketSeriesData[];
    marketMeta: MarketLineMeta[];
    /** Event is neg-risk (options mutually exclusive, YES probs sum to ~100%). */
    mutuallyExclusive?: boolean;
    loading: boolean;
    highlightedMarkets?: Set<string> | null;
    onMarketClick?: (conditionId: string) => void;
    resolution: CandleResolution;
    onResolutionChange: (res: CandleResolution) => void;
    timeRange: [Date, Date] | null;
    onTimeRangeChange: (range: [Date, Date] | null) => void;
    eventStartDate?: Date | null;
    eventEndDate?: Date | null;
    assetOverlays?: AssetOverlayData[];
    /**
     * Each displayed market's NO-side series, keyed by conditionId. Only ever
     * holds data for the window being drawn — the hook that produces it drops
     * anything stale — so it can be paired with the YES series without a guard
     * here.
     */
    noSeriesByMarket?: ReadonlyMap<string, OHLCVPoint[]>;
    tradeOverlay?: TradeOverlayData;
    /**
     * Every trade in the current window, as the trades sidebar has filtered it.
     * Drawn only when the Trades chip is on, and clustered so the count stays
     * readable.
     */
    windowTrades?: TradeOverlayData['trades'];
    signalOverlay?: SignalOverlayData;
    alertsOverlay?: AlertsOverlayData;
    showNoLine?: string | null;
    hoveredTraderAddress?: string | null;
    headerExtra?: React.ReactNode;
    convictionByGroup?: Map<string, ConvictionSeries>;
    convictionLoading?: boolean;
    traderSignals?: SignalItem[];
    onTraderHover?: (address: string | null) => void;
    convictionRawMode?: boolean;
    onConvictionRawModeChange?: (raw: boolean) => void;
    showVolume?: boolean;
    convictionControls?: React.ReactNode;
    convictionMetric?: ConvictionMetric;
    /**
     * The display mode to open in. A one-time seed, not a controlled value:
     * once the chart is mounted the user's choice wins, and a later change to
     * this prop is ignored. Supplying it also suppresses the auto-stacked
     * heuristic below, on the grounds that a caller passing this has already
     * made the decision the heuristic exists to guess at.
     */
    initialChartType?: EffectiveChartType;
    /** Notified of the effective display mode ('line' | 'candles'). */
    onChartTypeChange?: (type: 'line' | 'candles') => void;
    /** Key of the trade currently hovered (in the list or on the plot). */
    hoveredTradeKey?: string | null;
    /** Notified when a trade marker is hovered/unhovered on the plot. */
    onTradeHover?: (key: string | null) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ConvergenceChart({
    series,
    marketMeta,
    mutuallyExclusive = false,
    loading,
    highlightedMarkets,
    onMarketClick,
    resolution,
    onResolutionChange,
    timeRange,
    onTimeRangeChange,
    eventStartDate,
    eventEndDate,
    assetOverlays,
    noSeriesByMarket,
    tradeOverlay,
    windowTrades,
    signalOverlay,
    alertsOverlay,
    showNoLine,
    hoveredTraderAddress,
    headerExtra,
    convictionByGroup,
    convictionLoading,
    traderSignals,
    onTraderHover,
    showVolume = false,
    convictionRawMode = false,
    onConvictionRawModeChange,
    convictionControls,
    convictionMetric = 'net_capital',
    initialChartType,
    onChartTypeChange,
    hoveredTradeKey,
    onTradeHover,
}: ConvergenceChartProps) {
    const theme = useTheme();
    const noByMarket = noSeriesByMarket ?? EMPTY_NO_SERIES;
    const { ref: containerRef, width } = useContainerWidth();
    const svgRef = useRef<SVGSVGElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const posTooltipRef = useRef<HTMLDivElement>(null);

    // Current hovered trade key in a ref so the heavy render effect doesn't
    // re-run (full chart rebuild) on every hover; a light effect toggles it.
    const hoveredTradeKeyRef = useRef(hoveredTradeKey);
    hoveredTradeKeyRef.current = hoveredTradeKey;
    // trade key -> the mark drawn for it, which may be a cluster shared with
    // other trades. Lets a hovered list row light up its mark even when that
    // mark is not one-to-one with the trade.
    const tradeMarkIndexRef = useRef(new Map<string, SVGPathElement>());

    const [fullscreen, setFullscreen] = useState(false);
    const [showSignals, setShowSignals] = useState(true);
    const [showVol, setShowVol] = useState(showVolume ?? false);
    const [showPos, setShowPos] = useState(true);
    const [showConviction, setShowConviction] = useState(true);
    // Seeded, then owned locally. Note that the seed survives a cold start:
    // `chartType` holds what was asked for while `effectiveChartType` gates it
    // on what the data can actually support, so an initial 'candles' or
    // 'stacked' that is not yet drawable falls back to line and then takes
    // effect on its own once the series arrive.
    const [chartType, setChartType] = useState<EffectiveChartType>(
        initialChartType ?? 'line'
    );
    // Which side of the market the candlesticks show. Only ever meaningful for
    // a single market, which is the only case candlesticks are offered in.
    const [candleSide, setCandleSide] = useState<'yes' | 'no'>('yes');
    // The window's trades are off by default: it is a heavy overlay, and the
    // chart is a price chart first.
    const [showWindowTrades, setShowWindowTrades] = useState(false);
    const [overlayStats, setOverlayStats] = useState<OverlayStats | null>(null);
    const { fit, toggleFit } = useAxisFit();

    const viewport = useChartViewport(timeRange, onTimeRangeChange);
    const { liveDomain, commitRange } = viewport;

    const colorMap = useMemo(
        () => new Map(marketMeta.map(m => [m.conditionId, m.color])),
        [marketMeta]
    );
    const questionMap = useMemo(
        () => new Map(marketMeta.map(m => [m.conditionId, m.question])),
        [marketMeta]
    );
    const outcomesMap = useMemo(
        () => new Map(marketMeta.map(m => [m.conditionId, m.outcomes ?? null])),
        [marketMeta]
    );
    // NO side keeps red only where up/down-style direction makes red meaningful.
    const noSideColor = useCallback(
        (outcomes: string[] | null) =>
            isNeutralNoSide(outcomes)
                ? theme.palette.text.secondary
                : theme.palette.error.main,
        [theme]
    );
    const hasHighlight = !!(highlightedMarkets && highlightedMarkets.size > 0);

    // Candlesticks are only meaningful for a single visible market.
    const selectedIds = useMemo(
        () => displayedConditionIds(series, highlightedMarkets),
        [series, highlightedMarkets]
    );
    const candlesAllowed = canShowCandles(selectedIds);
    const candleMarketId = selectedIds.length === 1 ? selectedIds[0] : null;

    // A side is a statement about one market. Carrying it into a different
    // market would silently show the wrong outcome under the old label.
    useEffect(() => {
        setCandleSide('yes');
    }, [candleMarketId]);

    // Name the sides after the real outcomes where the market has them, so a
    // named market reads "Kacper Adamus / NO" rather than a bare YES / NO.
    const candleSideLabels = useMemo(() => {
        const outcomes = candleMarketId
            ? outcomesMap.get(candleMarketId) ?? null
            : null;
        // Badge length, not canvas length: these are DOM buttons with more room
        // than an on-canvas label has.
        return {
            yes: outcomeLabel(outcomes, 0, OUTCOME_MAXLEN_BADGE)?.text ?? 'YES',
            no: outcomeLabel(outcomes, 1, OUTCOME_MAXLEN_BADGE)?.text ?? 'NO',
        };
    }, [candleMarketId, outcomesMap]);

    // Number of series actually drawn on the chart right now.
    const displayedCount = useMemo(
        () => series.filter(s => s.data.length > 0).length,
        [series]
    );

    // Stacked (100%) is meaningful only when the options partition the outcome
    // space: either a mutually-exclusive (neg-risk) event, or a single market
    // where stacked shows YES (market colour) vs Others (= NO).
    const stackedAllowed =
        displayedCount >= 1 && (displayedCount === 1 || mutuallyExclusive);

    const effectiveChartType: EffectiveChartType =
        chartType === 'stacked' && stackedAllowed
            ? 'stacked'
            : chartType === 'candles' && candlesAllowed
              ? 'candles'
              : 'line';

    // Default to stacked for mutually-exclusive events with >= 5 lines drawn,
    // unless the mode has already been decided — either by the user this
    // session, or by the caller via initialChartType.
    const userSetChartType = useRef(initialChartType != null);
    useEffect(() => {
        if (userSetChartType.current) return;
        if (mutuallyExclusive && displayedCount >= 5) setChartType('stacked');
    }, [mutuallyExclusive, displayedCount]);

    // Let the parent gate auto-resolution-on-zoom by the actual display mode.
    // Stacked is time-continuous like line, so it reports as 'line' here.
    useEffect(() => {
        onChartTypeChange?.(
            effectiveChartType === 'candles' ? 'candles' : 'line'
        );
    }, [effectiveChartType, onChartTypeChange]);

    const handleResolutionChange = useCallback(
        (e: SelectChangeEvent<number>) =>
            onResolutionChange(Number(e.target.value) as CandleResolution),
        [onResolutionChange]
    );

    const displayRange = useMemo((): [Date, Date] | null => {
        if (timeRange) return timeRange;
        const allDates = series
            .filter(s => s.data.length > 0)
            .flatMap(s => s.data.map(d => d.t));
        if (allDates.length === 0) return null;
        return d3.extent(allDates) as [Date, Date];
    }, [timeRange, series]);

    const exportSeries = useMemo((): ExportSeries[] => {
        const result: ExportSeries[] = [];
        for (const s of series) {
            if (s.data.length === 0) continue;
            if (highlightedMarkets && !highlightedMarkets.has(s.conditionId))
                continue;
            const meta = marketMeta.find(m => m.conditionId === s.conditionId);
            result.push({
                label: meta?.question ?? s.conditionId,
                data: s.data,
            });
        }
        for (const overlay of assetOverlays ?? []) {
            if (overlay.data.length > 0)
                result.push({
                    label: overlay.ticker,
                    data: overlay.data,
                    isPrice: true,
                });
        }
        return result;
    }, [series, marketMeta, highlightedMarkets, assetOverlays]);

    // The context is optional: the chart also renders outside any provider.
    const signalInteractionCtx = (() => {
        try {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            return useSignalInteraction();
        } catch {
            return null;
        }
    })();
    const signalInteraction = signalOverlay ? signalInteractionCtx : null;

    // Close fullscreen on Escape
    useEffect(() => {
        if (!fullscreen) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setFullscreen(false);
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [fullscreen]);

    // ─── Main D3 render effect ────────────────────────────────────────────
    //
    // DRAW ORDER IS Z-ORDER. The sequence below is a spec, not an incidental
    // arrangement: SVG paints in document order and there is no z-index. Two
    // consequences worth stating, because they are invisible from any single
    // module:
    //
    //   - The chrome (crosshair, hover dots, axis pills) is created AFTER the
    //     plot and the viewport handlers, so it paints above them. The volume
    //     panel is drawn before it but drives it, which is why that panel
    //     returns an attach function instead of binding its own handlers.
    //   - The conviction signal group and the alert group are raised at the end
    //     of their own modules, so they win the pointer over the transparent
    //     hover rects beneath them.
    //
    // Anything reordered here changes what is clickable, not just what is on top.
    useEffect(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const seriesWithData = series.filter(s => s.data.length > 0);
        if (seriesWithData.length === 0 || width <= 0) return;

        // ── Theme ─────────────────────────────────────────────────────────
        const isDark = theme.palette.mode === 'dark';
        const textColor = theme.palette.text.disabled;
        const colors: ChartColors = {
            isDark,
            gridColor: isDark ? alpha('#fff', 0.06) : alpha('#000', 0.06),
            textColor,
            axisTextColor: isDark ? alpha('#fff', 0.5) : alpha('#000', 0.45),
            sepColor: isDark ? alpha('#fff', 0.1) : alpha('#000', 0.08),
            tc: makeTooltipColors(textColor, isDark),
        };

        // ── Which panels exist ────────────────────────────────────────────
        const hasPositionChart = !!(
            tradeOverlay && tradeOverlay.highlightedAccounts.size > 0
        );
        const allConvictionPoints: ConvictionTimePoint[] = [];
        if (convictionByGroup)
            for (const s of convictionByGroup.values())
                allConvictionPoints.push(...s.points);
        const hasConvictionPanel =
            !!convictionByGroup && allConvictionPoints.length > 0;

        const loadedAssets = (assetOverlays ?? []).filter(
            a => a.data.length > 0
        );

        const layout = computeLayout({
            width,
            fullscreen,
            containerHeight: containerRef.current?.clientHeight ?? null,
            hasPositionChart,
            hasConvictionPanel,
            showVol,
            showPos,
            showConviction,
            loadedAssetCount: loadedAssets.length,
        });
        svg.attr('height', layout.totalHeight);

        // ── Data and scales ───────────────────────────────────────────────
        const now = quantizedNow();
        const extendedSeries = extendSeriesToNow(
            seriesWithData,
            eventEndDate,
            now
        );
        const fullDomain = computeFullDomain(extendedSeries);
        const xDomain = liveDomain ?? timeRange ?? fullDomain;
        const xScale = makeXScale(xDomain, layout.w);
        const candleDataFor = makeCandleDataFor(candleSide, noByMarket);
        const probDomain = computeProbDomain({
            series: extendedSeries,
            chartType: effectiveChartType,
            fit,
            selectedIds,
            showNoLine,
            xDomain,
            candleDataFor,
        });
        const yScale = makeYScale(probDomain, layout.probHeight);

        // ── Clip paths ────────────────────────────────────────────────────
        const defs = svg.append('defs');
        const { clipIds } = layout;

        defs.append('clipPath')
            .attr('id', clipIds.prob)
            .append('rect')
            .attr('x', MARGIN.left)
            .attr('y', MARGIN.top)
            .attr('width', layout.w)
            .attr('height', layout.probHeight - MARGIN.top);
        defs.append('clipPath')
            .attr('id', clipIds.vol)
            .append('rect')
            .attr('x', MARGIN.left)
            .attr('y', layout.volTop)
            .attr('width', layout.w)
            .attr('height', layout.volHeight);
        if (layout.convVisible) {
            defs.append('clipPath')
                .attr('id', clipIds.conv)
                .append('rect')
                .attr('x', MARGIN.left)
                .attr('y', layout.convTop)
                .attr('width', layout.w)
                .attr('height', layout.convHeight);
        }
        // Markers overflow the plot rect, so they get a looser clip of their own.
        defs.append('clipPath')
            .attr('id', clipIds.sig)
            .append('rect')
            .attr('x', MARGIN.left - MARKER_OVERFLOW)
            .attr('y', MARGIN.top - MARKER_OVERFLOW)
            .attr('width', layout.w + MARKER_OVERFLOW * 2)
            .attr(
                'height',
                layout.probHeight - MARGIN.top + MARKER_OVERFLOW * 2
            );
        if (layout.convVisible) {
            defs.append('clipPath')
                .attr('id', clipIds.convSig)
                .append('rect')
                .attr('x', MARGIN.left - MARKER_OVERFLOW)
                .attr('y', layout.convTop - MARKER_OVERFLOW)
                .attr('width', layout.w + MARKER_OVERFLOW * 2)
                .attr('height', layout.convHeight + MARKER_OVERFLOW * 2);
        }

        const ctx: DrawCtx = {
            svg,
            defs,
            svgEl: svgRef.current,
            theme,
            colors,
            layout,
            now,
            xScale,
            yScale,
            xDomain,
            fullDomain,
            probDomain,
            series: extendedSeries,
            selectedIds,
            chartType: effectiveChartType,
            resolution,
            candleDataFor,
            colorMap,
            questionMap,
            outcomesMap,
            noSideColor,
            highlightedMarkets,
            hasHighlight,
            onMarketClick,
            showNoLine,
            tooltipRef,
            posTooltipRef,
        };

        // ── 1. Axes ───────────────────────────────────────────────────────
        drawXAxis(ctx);
        drawProbabilityAxis(ctx, fit, toggleFit);

        // ── 2. Volume panel (hover bound in step 5) ───────────────────────
        const attachVolumeHover = drawVolumePanel(ctx, {
            noByMarket,
            onToggle: () => setShowVol(v => !v),
        });

        // ── 3. Probability lines, then the asset overlays on top of them ──
        const probGroup = drawProbabilityPanel(ctx);
        const extendedAssetArrays = drawAssetOverlays(
            ctx,
            probGroup,
            loadedAssets
        );

        // ── 4. Viewport: brush, wheel, pan, edge glow ─────────────────────
        const bounds = computePanBounds(
            eventStartDate,
            eventEndDate,
            now,
            fullDomain
        );
        const brushGroup = attachViewport(ctx, {
            ...viewport,
            bounds,
            timeRange,
            onTimeRangeChange,
            svgRef,
        });

        // ── 5. Shared chrome, then everything that drives it ──────────────
        const chrome = createChrome(ctx, tooltipRef.current);
        attachVolumeHover(chrome);
        attachCrosshair(ctx, {
            brushGroup,
            chrome,
            rawSeries: series,
            loadedAssets,
            extendedAssetArrays,
        });

        // ── 6. Overlays and remaining panels ──────────────────────────────
        if (showSignals && signalOverlay && signalOverlay.byMarket.size > 0) {
            drawSignalOverlay(ctx, {
                signalOverlay,
                hoveredTraderAddress,
                signalInteraction,
                spanDays: xTickFormat(xDomain).spanDays,
                chrome,
            });
        }

        if (hasPositionChart) {
            drawPositionPanel(ctx, {
                tradeOverlay: tradeOverlay!,
                chrome,
                onToggle: () => setShowPos(v => !v),
            });
        }

        if (hasConvictionPanel && convictionByGroup) {
            drawConvictionPanel(ctx, {
                convictionByGroup,
                allConvictionPoints,
                convictionMetric,
                convictionRawMode,
                onConvictionRawModeChange,
                traderSignals,
                onTraderHover,
                eventEndDate,
                chrome,
                onToggle: () => setShowConviction(v => !v),
            });
        }

        drawTradeOverlay(ctx, {
            tradeOverlay,
            windowTrades,
            showWindowTrades,
            hoveredTradeKeyRef,
            tradeMarkIndexRef,
            setOverlayStats,
            onTradeHover,
        });

        if (alertsOverlay && alertsOverlay.alerts.length > 0) {
            drawAlertOverlay(ctx, { alertsOverlay, chrome });
        }
    }, [
        series,
        marketMeta,
        width,
        theme,
        highlightedMarkets,
        onMarketClick,
        timeRange,
        onTimeRangeChange,
        assetOverlays,
        fullscreen,
        tradeOverlay,
        signalOverlay,
        showSignals,
        showVol,
        showPos,
        showConviction,
        convictionByGroup,
        traderSignals,
        onTraderHover,
        signalInteraction?.state,
        convictionMetric,
        effectiveChartType,
        resolution,
        liveDomain,
        commitRange,
        showNoLine,
        showWindowTrades,
        windowTrades,
        candleSide,
        noByMarket,
        fit,
        toggleFit,
    ]);

    // Light-weight hover emphasis: toggle just the matching trade marker's
    // styling instead of rebuilding the whole chart on every hover.
    useEffect(() => {
        const svgEl = svgRef.current;
        if (!svgEl) return;
        const hovEl = hoveredTradeKey
            ? tradeMarkIndexRef.current.get(hoveredTradeKey) ?? null
            : null;
        d3.select(svgEl)
            .selectAll<SVGPathElement, unknown>('.trade-marker')
            .each(function () {
                const el = d3.select(this);
                const isHov = hovEl !== null && this === hovEl;
                el.attr(
                    'fill-opacity',
                    isHov ? 0.95 : el.attr('data-base-fillop')
                )
                    .attr(
                        'stroke-opacity',
                        isHov ? 1 : el.attr('data-base-strokeop')
                    )
                    .attr('stroke-width', isHov ? 1.5 : 0.5);
            });
    }, [hoveredTradeKey]);

    // ─── Render ───────────────────────────────────────────────────────────

    const hasSeries = series.some(s => s.data.length > 0);
    const eventProgress = useMemo(
        () => computeEventProgress(eventStartDate, eventEndDate, timeRange),
        [eventStartDate, eventEndDate, timeRange]
    );

    return (
        <>
            {fullscreen && (
                <Box
                    onClick={() => setFullscreen(false)}
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        bgcolor: 'rgba(0,0,0,0.6)',
                        zIndex: 1299,
                    }}
                />
            )}
            <Box
                sx={
                    fullscreen
                        ? {
                              position: 'fixed',
                              top: '2.5vh',
                              left: '2.5vw',
                              width: '95vw',
                              height: '95vh',
                              zIndex: 1300,
                              '& .convergence-chart-container': {
                                  height: 'calc(95vh - 120px)',
                              },
                          }
                        : undefined
                }
            >
                <SectionCard
                    sx={{
                        mb: fullscreen ? 0 : 2,
                        height: fullscreen ? '100%' : undefined,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {eventProgress && (
                        <EventProgressBar
                            progress={eventProgress}
                            timeRange={timeRange}
                        />
                    )}

                    <PanelHeader title="Probability Convergence" icon="◈">
                        <ChartHeaderControls
                            headerExtra={headerExtra}
                            displayRange={displayRange}
                            timeRange={timeRange}
                            onTimeRangeChange={onTimeRangeChange}
                            hasSignalOverlay={!!signalOverlay}
                            showSignals={showSignals}
                            onToggleSignals={() => setShowSignals(s => !s)}
                            hasWindowTrades={
                                !!windowTrades && windowTrades.length > 0
                            }
                            showWindowTrades={showWindowTrades}
                            onToggleWindowTrades={() =>
                                setShowWindowTrades(t => !t)
                            }
                            overlayStats={overlayStats}
                            convictionControls={convictionControls}
                            effectiveChartType={effectiveChartType}
                            candlesAllowed={candlesAllowed}
                            stackedAllowed={stackedAllowed}
                            onChartTypeSelect={v => {
                                userSetChartType.current = true;
                                setChartType(v);
                            }}
                            candleSide={candleSide}
                            candleSideLabels={candleSideLabels}
                            onCandleSideSelect={setCandleSide}
                            resolution={resolution}
                            onResolutionChange={handleResolutionChange}
                            exportSeries={exportSeries}
                            exportFilenamePrefix={
                                marketMeta[0]?.question ?? 'convergence'
                            }
                            fullscreen={fullscreen}
                            onToggleFullscreen={() => setFullscreen(f => !f)}
                        />
                    </PanelHeader>

                    <Box
                        ref={containerRef}
                        className="convergence-chart-container"
                        sx={{
                            position: 'relative',
                            px: 1,
                            pb: 1,
                            flex: fullscreen ? 1 : undefined,
                            // Prevent text/label selection while dragging to pan.
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                        }}
                    >
                        {loading ? (
                            <Skeleton
                                variant="rectangular"
                                height={TOTAL_HEIGHT}
                                sx={{ borderRadius: 1, m: 1 }}
                            />
                        ) : !hasSeries ? (
                            <Box
                                sx={{
                                    height: TOTAL_HEIGHT,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        color: 'text.disabled',
                                    }}
                                >
                                    No probability data available
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <svg
                                    ref={svgRef}
                                    width={width}
                                    height={TOTAL_HEIGHT}
                                    style={{ display: 'block' }}
                                />
                                <TooltipBox
                                    ref={tooltipRef}
                                    minWidth={140}
                                    maxWidth={240}
                                />
                                <TooltipBox
                                    ref={posTooltipRef}
                                    minWidth={160}
                                    maxWidth={260}
                                />
                            </>
                        )}
                    </Box>

                    {hasSeries && !loading && (
                        <MarketLegend
                            marketMeta={marketMeta}
                            highlightedMarkets={highlightedMarkets}
                            onMarketClick={onMarketClick}
                        />
                    )}
                </SectionCard>
            </Box>
        </>
    );
}
